import { computed, toValue, watch } from 'vue';
import { getSportLiveTvCategoriesEventsPage } from '@/api/sport-live-tv';
import { useAsyncData } from '@/composables/use-async-data';
import { SPORT_LIVE_TV_EVENTS_LIMIT, SPORT_LIVE_TV_STATUSES } from '@/sport-live-tv/constants';
import { arrangeSportLiveTvEvents, groupSportLiveTvEventsByCategory, uniqueSportLiveTvEvents } from '@/sport-live-tv/helpers';
import { useSportLiveTvOverview } from '@/sport-live-tv/composables/use-sport-live-tv-overview';

export const useSportLiveTvRows = ({ status, sort, isFiltered }) => {
  const overview = useSportLiveTvOverview(status);

  const filledCategoryIds = computed(() => overview.categories.value.filter(({ events }) => events.length > 0).map(({ id }) => id));

  const categoryEvents = useAsyncData(() => getSportLiveTvCategoriesEventsPage({ categoryIds: filledCategoryIds.value, status: toValue(status), offset: 0, limit: SPORT_LIVE_TV_EVENTS_LIMIT }), {
    immediate: false,
  });

  watch(
    [filledCategoryIds, () => toValue(isFiltered)],
    ([categoryIds, filtered]) => {
      if (filtered || !categoryIds.length) {
        categoryEvents.reset();
        return;
      }

      categoryEvents.execute();
    },
    { immediate: true }
  );

  const eventsByCategory = computed(() => groupSportLiveTvEventsByCategory(categoryEvents.data.value?.events ?? []));

  const filledCategories = computed(() => overview.categories.value.map((category) => ({ ...category, events: eventsByCategory.value.get(category.id) ?? category.events })));

  const rows = computed(() =>
    filledCategories.value.map((category) => ({ ...category, events: arrangeSportLiveTvEvents(category.events, toValue(status), toValue(sort)) })).filter(({ events }) => events.length > 0)
  );

  const featured = computed(() => arrangeSportLiveTvEvents(overview.popular.value, toValue(status), toValue(sort)));

  const live = computed(() => {
    if (toValue(status) === SPORT_LIVE_TV_STATUSES.upcoming) return [];

    const events = uniqueSportLiveTvEvents(filledCategories.value.flatMap((category) => category.events));

    return arrangeSportLiveTvEvents(events, SPORT_LIVE_TV_STATUSES.live, toValue(sort));
  });

  return {
    categories: overview.categories,
    rows,
    featured,
    live,
    isLoading: overview.isLoading,
    isError: overview.isError,
    refetch: overview.refetch,
  };
};
